import ExpansionPanel from '@material-ui/core/ExpansionPanel'
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails'
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary'
import { withStyles } from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import cn from 'classnames'
import { groupBy, map as lodashMap, times } from 'lodash'
import { useState } from 'react'

import Skeleton from '../Skeleton'
import TableRowHeaderContent from '../Table/TableRowHeaderContent'

const styles = theme => ({
  wrapper: {
    '& > div:first-child': {
      borderRadius: '4px 4px 0 0',
    },
    '& > div:only-child': {
      borderRadius: '4px',
    },
  },
  root: {
    marginBottom: '-1px',
    border: theme.borders.default,
    boxShadow: 'none',
    '&$expanded': {
      margin: '0',
    },
  },
  summaryRoot: {
    padding: '0 16px',
  },
  summaryContent: {
    fontSize: theme.typography.fontSizeSm,
    fontWeight: theme.typography.fontWeightBolder,
  },
  summaryWrap: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& > div': {
      marginRight: '3px',
    },
    '& > div:not(.noMargin)': {
      marginRight: '16px',
    },
  },
  rowLabel: {
    color: `var(--gameday-body-textColor, ${theme.body.textColor})`,
    fontWeight: theme.typography.fontWeightBolder,
  },
  detailRoot: {
    padding: '0 16px 16px',
    fontSize: theme.typography.fontSizeSmaller,
  },
  fields: {
    width: '100%',
  },
  fieldWrapper: {
    display: 'flex',
    width: '100%',
    padding: '6px 0',
    alignItems: 'center',
    borderBottom: theme.borders.default,
    [theme.breakpoints.down(360)]: {
      flexDirection: 'column',
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  fieldLabel: {
    width: '40%',
    fontWeight: theme.typography.fontWeightBolder,
    color: `var(--gameday-table-tableHeaderTextColor, ${theme.table.tableHeaderTextColor})`,
    [theme.breakpoints.down(440)]: {
      width: '50%',
    },
    [theme.breakpoints.down(360)]: {
      width: '100%',
      paddingBottom: '6px',
    },
  },
  fieldText: {
    [theme.breakpoints.up(360)]: {
      width: '100%',
    },
  },
  actions: {
    width: '100%',
  },
  expanded: {
    borderColor: theme.borders.borderColorActive,
    '& $summaryRoot': {
      minHeight: '48px',
    },
    '& $summaryContent': {
      margin: '12px 0',
    },
  },
  skeletonWrap: {
    '& > div': {
      marginBottom: '2px',
    },
    '& > div:first-child': {
      borderRadius: '4px 4px 0 0',
    },
    '& > div:last-child': {
      borderRadius: '0 0 4px 4px',
    },
    '& > div:only-child': {
      borderRadius: '4px',
    },
  },
  noData: {
    width: '100%',
    textAlign: 'center',
    fontWeight: theme.typography.fontWeightBold,
    padding: '16px 0',
  },
})

const ResponsiveTable = ({
  classes,
  data,
  excludePrimaryFromDetails,
  headers,
  isMobile,
  loading,
  marginBottom,
  pageSize,
  rowId,
  tableGroupBy,
  tableGroupDescription,
  tableGroupHeaderSize,
  tableGroupCount,
  tableGroupActions,
}) => {
  const [expanded, setExpanded] = useState(false)

  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
  const renderCellContent = (column, data, row) => {
    if ('getValue' in column) {
      return column.getValue(row, column, isMobile)
    }
    return (
      <div className={cn(classes.rowLabel, { noMargin: column.noMargin })} key={column.name}>
        {row[column.name]}
      </div>
    )
  }

  const createListItemTitle = (data, columns, row) => {
    const primaryColumns = columns.filter(column => column.primary)
    return (
      <div className={classes.summaryWrap}>
        {primaryColumns.length === 0
          ? renderCellContent(columns[0], data, row)
          : primaryColumns
              .map(column => renderCellContent(column, data, row))
              .reduce((prev, next) => [prev, ' ', next])}
      </div>
    )
  }

  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
  const createListItemDescription = (data, columns, row, excludePrimary) => (
    <div className={classes.fields}>
      {columns
        .filter(column => (!excludePrimary || !column.primary) && !column.isHidden)
        // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
        .map((column, index) => (
          <div key={column.name} className={classes.fieldWrapper}>
            <div className={classes.fieldLabel}>{column.text}</div>
            <div className={classes.fieldText}>
              {!column.getValue ? row[column.name] : column.getValue(row, column)}
            </div>
          </div>
        ))}
    </div>
  )

  // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
  const handleChange = panel => (e, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  return (
    <>
      {loading && (
        <div className={classes.skeletonWrap}>
          {/** biome-ignore lint/correctness/noUnusedVariables: TODO */}
          {times(pageSize, i => (
            <Skeleton height={64} variant="rect" className={classes.skeleton} />
          ))}
        </div>
      )}
      <div className={classes.wrapper} style={{ marginBottom: `${marginBottom}px` || '8px' }}>
        {!tableGroupBy
          ? // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
            lodashMap(data, (row, index) => (
              <ExpansionPanel
                expanded={expanded === `panel-${row[rowId]}`}
                onChange={handleChange(`panel-${row[rowId]}`)}
                key={row[rowId]}
                classes={{
                  root: classes.root,
                  expanded: classes.expanded,
                }}
              >
                <ExpansionPanelSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${row[rowId]}bh-content`}
                  id={`panel${row[rowId]}bh-header`}
                  classes={{
                    root: classes.summaryRoot,
                    content: classes.summaryContent,
                  }}
                >
                  {createListItemTitle(data, headers, row, isMobile)}
                </ExpansionPanelSummary>
                <ExpansionPanelDetails classes={{ root: classes.detailRoot }}>
                  {createListItemDescription(data, headers, row, excludePrimaryFromDetails)}
                </ExpansionPanelDetails>
              </ExpansionPanel>
            ))
          : // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
            lodashMap(groupBy(data, tableGroupBy), (row, index) => (
              <>
                <TableRowHeaderContent
                  count={row.length}
                  heading={row[0][tableGroupBy]}
                  tableGroupDescription={tableGroupDescription}
                  tableGroupHeaderSize={tableGroupHeaderSize}
                  tableGroupCount={tableGroupCount}
                  tableGroupActions={tableGroupActions}
                  isMobile={isMobile}
                />
                {/** biome-ignore lint/correctness/noUnusedFunctionParameters: TODO */}
                {lodashMap(row, (rowData, i) => (
                  <ExpansionPanel
                    expanded={expanded === `panel-${rowData[rowId]}`}
                    onChange={handleChange(`panel-${rowData[rowId]}`)}
                    key={rowData[rowId]}
                    classes={{
                      root: classes.root,
                      expanded: classes.expanded,
                    }}
                  >
                    <ExpansionPanelSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${rowData[rowId]}bh-content`}
                      id={`panel${rowData[rowId]}bh-header`}
                      classes={{
                        root: classes.summaryRoot,
                        content: classes.summaryContent,
                      }}
                    >
                      {createListItemTitle(data, headers, rowData, isMobile)}
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails classes={{ root: classes.detailRoot }}>
                      {createListItemDescription(data, headers, rowData, excludePrimaryFromDetails)}
                    </ExpansionPanelDetails>
                  </ExpansionPanel>
                ))}
              </>
            ))}
        {data.length === 0 && <div className={classes.noData}>No Data</div>}
      </div>
    </>
  )
}

ResponsiveTable.defaultProps = {
  data: [],
  excludePrimaryFromDetails: false,
  headers: [],
  isMobile: true,
  rowId: 'id',
}

const ResponsiveComponent = withStyles(styles)(ResponsiveTable)
ResponsiveComponent.displayName = 'ResponsiveTable'
export default ResponsiveComponent
